import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Building2, MapPin, ArrowRight, Pencil, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { eur, STATO_LABELS, STATO_BADGE } from "@/lib/format";
import { cx } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CantiereModal from "@/components/CantiereModal";

export default function Cantieri() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("attivo");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await api.get("/cantieri");
    setItems(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (c, e) => { e.stopPropagation(); setEditing(c); setModalOpen(true); };

  const FILTERS = [
    { key: "attivo", label: "Aperte" },
    { key: "in_pausa", label: "Sospese" },
    { key: "completato", label: "Chiuse" },
    { key: "all", label: "Tutte" },
  ];
  const counts = items.reduce((acc, c) => { acc[c.stato] = (acc[c.stato] || 0) + 1; return acc; }, {});
  const visible = filter === "all" ? items : items.filter((c) => c.stato === filter);

  const confirmDelete = async () => {
    try {
      await api.del(`/cantieri/${toDelete.id}`);
      toast.success("Cantiere eliminato.");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Cantieri & Commesse</h1>
          <p className="text-sm text-muted-foreground mt-1">Gestisci e monitora la redditività di ogni progetto.</p>
        </div>
        <Button onClick={openNew} data-testid="add-cantiere-button"
          className="gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
          <Plus className="h-4 w-4" /> Nuovo Cantiere
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="cantieri-filter-bar">
        {FILTERS.map((f) => {
          const count = f.key === "all" ? items.length : (counts[f.key] || 0);
          return (
            <button key={f.key} data-testid={`cantieri-filter-${f.key}`} onClick={() => setFilter(f.key)}
              className={cx(
                "rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
                filter === f.key
                  ? "bg-slate-900 text-white border-slate-900 dark:bg-amber-500 dark:text-slate-900 dark:border-amber-500"
                  : "bg-transparent text-muted-foreground hover:text-foreground border-border"
              )}>
              {f.label} <span className="ml-1 opacity-70">{count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-56 rounded-xl bg-secondary animate-pulse" />)}
        </div>
      ) : visible.length === 0 ? (
        <Card><CardContent className="py-16 text-center">
          <Building2 className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-medium">{items.length === 0 ? "Nessun cantiere ancora" : "Nessun cantiere in questa categoria"}</p>
          <p className="text-sm text-muted-foreground">{items.length === 0 ? "Crea il tuo primo cantiere per iniziare a tracciare i costi." : "Cambia filtro per vedere gli altri cantieri."}</p>
          {items.length === 0 && <Button onClick={openNew} className="mt-4 gap-2" data-testid="empty-add-cantiere-button"><Plus className="h-4 w-4" /> Nuovo Cantiere</Button>}
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {visible.map((c) => (
            <Card key={c.id} data-testid={`cantiere-card-${c.id}`}
              onClick={() => navigate(`/cantieri/${c.id}`)}
              className="cursor-pointer group hover:border-amber-400 dark:hover:border-amber-500/60 transition-all animate-fade-up">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-base truncate group-hover:text-amber-600 transition-colors">{c.nome}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{c.codice_commessa || "—"} · {c.cliente || "Cliente n/d"}</p>
                  </div>
                  <span className={cx("shrink-0 rounded-full border px-2.5 py-0.5 text-[11px] font-medium", STATO_BADGE[c.stato])}>
                    {STATO_LABELS[c.stato]}
                  </span>
                </div>

                {c.indirizzo && (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground truncate">
                    <MapPin className="h-3.5 w-3.5 shrink-0" /> {c.indirizzo}
                  </p>
                )}

                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-secondary py-2">
                    <p className="text-[10px] uppercase text-muted-foreground">{isOwner ? "Ricavi" : "Budget"}</p>
                    <p className="text-sm font-semibold font-mono text-emerald-600">{eur(isOwner ? c.ricavi : c.budget_stimato)}</p>
                  </div>
                  <div className="rounded-lg bg-secondary py-2">
                    <p className="text-[10px] uppercase text-muted-foreground">Costi</p>
                    <p className="text-sm font-semibold font-mono text-red-600">{eur(c.costi)}</p>
                  </div>
                  <div className="rounded-lg bg-secondary py-2">
                    <p className="text-[10px] uppercase text-muted-foreground">Margine</p>
                    <p className={cx("text-sm font-semibold font-mono", c.margine >= 0 ? "text-slate-900 dark:text-slate-100" : "text-red-600")}>{c.margine_pct}%</p>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between text-[11px] text-muted-foreground">
                    <span>Avanzamento (SAL)</span><span>{c.sal_percentuale}%</span>
                  </div>
                  <Progress value={c.sal_percentuale} className="h-2" />
                </div>

                <div className="flex items-center justify-between pt-1">
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      data-testid={`edit-cantiere-${c.id}`} onClick={(e) => openEdit(c, e)}><Pencil className="h-4 w-4" /></Button>
                    {isOwner && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      data-testid={`delete-cantiere-${c.id}`} onClick={(e) => { e.stopPropagation(); setToDelete(c); }}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                  <span className="flex items-center gap-1 text-xs font-medium text-amber-600">Dettaglio <ArrowRight className="h-3.5 w-3.5" /></span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CantiereModal open={modalOpen} onOpenChange={setModalOpen} initial={editing} onSaved={load} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-cantiere-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il cantiere?</AlertDialogTitle>
            <AlertDialogDescription>
              Il cantiere "{toDelete?.nome}" verrà eliminato. Le transazioni collegate diventeranno "Generale". L'azione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-cantiere">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-cantiere" className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
